import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { sendWhatsAppMessage } from '../lib/whatsapp';
import { initFacebookSDK, loginWithFacebook, generateVerifyToken } from '../lib/facebook';
import {
  Plus,
  MessageSquare,
  Send,
  Edit,
  History,
  AlertCircle,
  Bot,
  Loader2,
  PhoneCall,
  Facebook,
  Megaphone
} from 'lucide-react';

interface Instance {
  id: string;
  name: string;
  phone_number_id: string;
  access_token: string;
  verify_token: string;
  created_at: string;
  is_active: boolean;
  messages_sent: number;
  messages_received: number;
}

interface NewInstance {
  name: string;
}

interface TestMessage {
  to: string;
  template: {
    name: string;
    language: {
      code: string;
    };
  };
}

export default function Channels() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [instances, setInstances] = useState<Instance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNewInstanceModal, setShowNewInstanceModal] = useState(false);
  const [showTestMessageModal, setShowTestMessageModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState<Instance | null>(null);
  const [editingInstance, setEditingInstance] = useState<Instance | null>(null);
  const [sending, setSending] = useState(false);
  const [fbLoading, setFbLoading] = useState(false);
  const [fbReady, setFbReady] = useState(false);
  const [testMessage, setTestMessage] = useState<TestMessage>({
    to: '',
    template: {
      name: 'agentia',
      language: {
        code: 'es_AR'
      }
    }
  });
  const [newInstance, setNewInstance] = useState<NewInstance>({
    name: ''
  });

  useEffect(() => {
    const initFB = async () => {
      try {
        await initFacebookSDK();
        setFbReady(true);
      } catch (err: any) {
        console.error('Failed to initialize Facebook SDK:', err);
        setError('Error al inicializar Facebook SDK: ' + err.message);
        setFbReady(false);
      }
    };
    
    initFB();
  }, []);

  useEffect(() => {
    fetchInstances();
  }, [user]);

  async function fetchInstances() {
    try {
      if (!user?.id) {
        setError('Usuario no autenticado');
        setLoading(false);
        return;
      }

      let retries = 3;
      let lastError;

      while (retries > 0) {
        try {
          const { data, error: fetchError } = await supabase
            .from('instances')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });

          if (fetchError) {
            throw fetchError;
          }

          setInstances(data || []);
          
          if (data && data.length === 1) {
            setSelectedInstance(data[0]);
          }
          
          setError('');
          break;
        } catch (err) {
          lastError = err;
          retries--;
          if (retries > 0) {
            await new Promise(resolve => setTimeout(resolve, (3 - retries) * 1000));
          }
        }
      }

      if (retries === 0 && lastError) {
        throw lastError;
      }
    } catch (err: any) {
      console.error('Error in fetchInstances:', err);
      
      if (!navigator.onLine) {
        setError('No hay conexión a internet. Por favor, verifica tu conexión y vuelve a intentarlo.');
      } else if (err.code === 'PGRST301') {
        setError('Error de autenticación. Por favor, vuelve a iniciar sesión.');
        navigate('/login');
      } else {
        setError(err.message || 'Error al cargar los canales. Por favor, intente nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  }

  const handleCreateInstance = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!fbReady) {
      setError('Facebook SDK no está inicializado. Por favor, espere un momento y vuelva a intentarlo.');
      return;
    }

    try {
      if (!user?.id) {
        setError('Usuario no autenticado');
        return;
      }

      if (!newInstance.name.trim()) {
        setError('El nombre del canal es requerido');
        return;
      }

      setFbLoading(true);
      setError('');

      // Get Facebook access token and phone number ID
      const fbResponse = await loginWithFacebook();
      
      if (!fbResponse || !fbResponse.accessToken || !fbResponse.phoneNumberId) {
        throw new Error('No se pudo obtener la información necesaria de Facebook');
      }

      const { accessToken, phoneNumberId } = fbResponse;
      
      // Generate verify token
      const verifyToken = generateVerifyToken();

      // Create instance
      const { error: insertError } = await supabase.from('instances').insert({
        user_id: user.id,
        name: newInstance.name.trim(),
        phone_number_id: phoneNumberId,
        access_token: accessToken,
        verify_token: verifyToken,
        is_active: true
      });

      if (insertError) {
        if (insertError.code === '23505') {
          throw new Error('Ya existe un canal con este número de teléfono');
        }
        throw new Error('Error al crear el canal: ' + insertError.message);
      }

      setShowNewInstanceModal(false);
      setNewInstance({ name: '' });
      await fetchInstances();
      
    } catch (err: any) {
      console.error('Error in handleCreateInstance:', err);
      setError(err.message || 'Error al crear el canal');
    } finally {
      setFbLoading(false);
    }
  };

  async function handleUpdateInstance(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (!editingInstance?.id) {
        setError('No se encontró el ID del canal');
        return;
      }

      const { error: updateError } = await supabase
        .from('instances')
        .update({
          name: editingInstance.name,
          phone_number_id: editingInstance.phone_number_id,
          access_token: editingInstance.access_token,
          verify_token: editingInstance.verify_token,
          is_active: editingInstance.is_active,
        })
        .eq('id', editingInstance.id);

      if (updateError) {
        throw new Error('Error al actualizar el canal: ' + updateError.message);
      }

      setShowEditModal(false);
      setEditingInstance(null);
      await fetchInstances();
    } catch (err: any) {
      setError(err.message || 'Error al actualizar el canal');
      console.error('Error:', err);
    }
  }

  async function handleSendTestMessage(e: React.FormEvent) {
    e.preventDefault();
    
    if (!selectedInstance) {
      setError('No hay un canal seleccionado');
      return;
    }

    if (!selectedInstance.id) {
      setError('El canal seleccionado no tiene un ID válido');
      return;
    }

    if (!testMessage.to || !testMessage.template.name || !testMessage.template.language.code) {
      setError('Por favor complete todos los campos del mensaje');
      return;
    }

    const phoneRegex = /^\+[1-9]\d{1,14}$/;
    if (!phoneRegex.test(testMessage.to)) {
      setError('El número de teléfono debe incluir el código de país y seguir el formato E.164 (Ej: +34612345678)');
      return;
    }

    setSending(true);
    setError('');

    try {
      await sendWhatsAppMessage(
        selectedInstance.id,
        testMessage.to,
        testMessage.template
      );
      
      setShowTestMessageModal(false);
      setTestMessage({
        to: '',
        template: {
          name: 'agentia',
          language: {
            code: 'es_AR'
          }
        }
      });
    } catch (err: any) {
      if (err.message === 'TOKEN_EXPIRED') {
        try {
          // Get new Facebook access token
          const fbResponse = await loginWithFacebook();
          
          if (!fbResponse || !fbResponse.accessToken) {
            throw new Error('No se pudo obtener un nuevo token de acceso');
          }

          // Update instance with new access token
          const { error: updateError } = await supabase
            .from('instances')
            .update({ access_token: fbResponse.accessToken })
            .eq('id', selectedInstance.id);

          if (updateError) {
            throw new Error('Error al actualizar el token de acceso');
          }

          // Retry sending the message
          await sendWhatsAppMessage(
            selectedInstance.id,
            testMessage.to,
            testMessage.template
          );

          setShowTestMessageModal(false);
          setTestMessage({
            to: '',
            template: {
              name: 'agentia',
              language: {
                code: 'es_AR'
              }
            }
          });
        } catch (refreshErr: any) {
          setError(
            'El token de acceso ha expirado. Por favor, vuelva a conectar su cuenta de Facebook ' +
            'haciendo clic en el botón "Conectar WhatsApp" en la parte superior de la página.'
          );
        }
      } else if (err.message.includes("no está en la lista de autorizados")) {
        setError(
          'El número de teléfono del destinatario no está en la lista de autorizados. ' +
          'Para enviar mensajes de prueba, primero debe agregar este número a la lista de números autorizados ' +
          'en su cuenta de WhatsApp Business.'
        );
      } else {
        setError(err.message || 'Error al enviar el mensaje de prueba');
      }
      console.error('Error:', err);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="h-16 bg-white border-b flex items-center justify-between px-8">
        <div className="flex items-center">
          <h1 className="text-xl font-semibold text-gray-800">Canales</h1>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/connect-whatsapp')}
            className="flex items-center px-4 py-2 bg-[#25D366] text-white rounded-md hover:bg-[#128C7E]"
          >
            <PhoneCall className="h-5 w-5 mr-2" />
            Conectar WhatsApp
          </button>
          <button
            onClick={() => setShowNewInstanceModal(true)}
            className="flex items-center px-4 py-2 bg-[#28A745] text-white rounded-md hover:bg-[#218838]"
          >
            <Plus className="h-5 w-5 mr-2" />
            Agregar canal
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-8">
        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-800">Canales</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Canal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha de Creación
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                      Cargando canales...
                    </td>
                  </tr>
                ) : instances.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                      No hay canales configurados
                    </td>
                  </tr>
                ) : (
                  instances.map((instance) => (
                    <tr 
                      key={instance.id} 
                      className={`hover:bg-gray-50 cursor-pointer ${selectedInstance?.id === instance.id ? 'bg-blue-50' : ''}`}
                      onClick={() => setSelectedInstance(instance)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
                            <MessageSquare className="h-4 w-4 text-gray-500" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{instance.name}</div>
                            <div className="text-sm text-gray-500">{instance.phone_number_id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(instance.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          instance.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {instance.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedInstance(instance);
                              setShowTestMessageModal(true);
                              setError('');
                            }}
                            className="px-3 py-1 bg-[#17A2B8] text-white rounded hover:bg-[#138496]"
                            title="Enviar mensaje de prueba"
                          >
                            <Send className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/history/${instance.id}`);
                            }}
                            className="px-3 py-1 bg-[#6C757D] text-white rounded hover:bg-[#5a6268]"
                            title="Ver historial de mensajes"
                          >
                            <History className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/chatbot/${instance.id}`);
                            }}
                            className="px-3 py-1 bg-[#17A2B8] text-white rounded hover:bg-[#138496] flex items-center"
                            title="Configurar chatbot"
                          >
                            <Bot className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/campaigns/${instance.id}`);
                            }}
                            className="px-3 py-1 bg-[#FFC107] text-white rounded hover:bg-[#E0A800] flex items-center"
                            title="Gestionar campañas"
                          >
                            <Megaphone className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingInstance(instance);
                              setShowEditModal(true);
                              setError('');
                            }}
                            className="px-3 py-1 bg-[#007BFF] text-white rounded hover:bg-[#0056b3]"
                            title="Editar canal"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* New Instance Modal */}
      {showNewInstanceModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleCreateInstance}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Nuevo Canal de WhatsApp
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                        Nombre del Canal
                      </label>
                      <input
                        type="text"
                        id="name"
                        value={newInstance.name}
                        onChange={(e) => setNewInstance({ name: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Ingresa un nombre descriptivo para identificar este canal
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={!fbReady || fbLoading}
                    className="w-full inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#1877F2] text-base font-medium text-white hover:bg-[#166FE5] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1877F2] sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {fbLoading ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Conectando...
                      </>
                    ) : (
                      <>
                        <Facebook className="h-5 w-5 mr-2" />
                        Conectar con Facebook
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewInstanceModal(false);
                      setNewInstance({ name: '' });
                      setError('');
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Instance Modal */}
      {showEditModal && editingInstance && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleUpdateInstance}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Editar Canal de WhatsApp
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700">
                        Nombre del Canal
                      </label>
                      <input
                        type="text"
                        id="edit-name"
                        value={editingInstance.name}
                        onChange={(e) => setEditingInstance({ ...editingInstance, name: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="edit-phone_number_id" className="block text-sm font-medium text-gray-700">
                        Phone Number ID
                      </label>
                      <input
                        type="text"
                        id="edit-phone_number_id"
                        value={editingInstance.phone_number_id}
                        onChange={(e) => setEditingInstance({ ...editingInstance, phone_number_id: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="edit-access_token" className="block text-sm font-medium text-gray-700">
                        Access Token
                      </label>
                      <input
                        type="text"
                        id="edit-access_token"
                        value={editingInstance.access_token}
                        onChange={(e) => setEditingInstance({ ...editingInstance, access_token: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="edit-verify_token" className="block text-sm font-medium text-gray-700">
                        Verify Token
                      </label>
                      <input
                        type="text"
                        id="edit-verify_token"
                        value={editingInstance.verify_token}
                        onChange={(e) => setEditingInstance({ ...editingInstance, verify_token: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editingInstance.is_active}
                          onChange={(e) => setEditingInstance({ ...editingInstance, is_active: e.target.checked })}
                          className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-600">Canal activo</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#007BFF] text-base font-medium text-white hover:bg-[#0056b3] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Guardar Cambios
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingInstance(null);
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Test Message Modal */}
      {showTestMessageModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSendTestMessage}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Enviar Mensaje de Prueba
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="to" className="block text-sm font-medium text-gray-700">
                        Número de WhatsApp
                      </label>
                      <input
                        type="text"
                        id="to"
                        value={testMessage.to}
                        onChange={(e) => setTestMessage({ ...testMessage, to: e.target.value })}
                        placeholder="Ej: +34612345678"
                        pattern="\+[1-9]\d{1,14}"
                        title="El número de teléfono debe incluir el código de país y seguir el formato E.164 (ej: +34612345678)"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        El número debe incluir el código de país con el signo + (Ej: +34612345678)
                      </p>
                    </div>
                    <div>
                      <label htmlFor="template-name" className="block text-sm font-medium text-gray-700">
                        Nombre de la Plantilla
                      </label>
                      <input
                        type="text"
                        id="template-name"
                        value={testMessage.template.name}
                        onChange={(e) => setTestMessage({
                          ...testMessage,
                          template: {
                            ...testMessage.template,
                            name: e.target.value
                          }
                        })}
                        placeholder="Ej: agentia"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="template-language" className="block text-sm font-medium text-gray-700">
                        Código de Idioma
                      </label>
                      <input
                        type="text"
                        id="template-language"
                        value={testMessage.template.language.code}
                        onChange={(e) => setTestMessage({
                          ...testMessage,
                          template: {
                            ...testMessage.template,
                            language: {
                              code: e.target.value
                            }
                          }
                        })}
                        placeholder="Ej: es_AR"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#28A745] text-base font-medium text-white hover:bg-[#218838] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {sending ? 'Enviando...' : 'Enviar Mensaje'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowTestMessageModal(false);
                      setTestMessage({
                        to: '',
                        template: {
                          name: 'agentia',
                          language: {
                            code: 'es_AR'
                          }
                        }
                      });
                      setError('');
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}